<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { QueryParams } from '@client/utils/query-params';

  import { clickOutside } from '@client/actions/click-outside';

  import { debounce } from '@common/utils/debounce';

  export let search = '';
  export let onChange = () => {};
  export let searchFields = [];
  export let selectedSearchField = '';

  let searchRef = null;
  let searchActive = false;
  let searchFocus = false;
  let selectedField = searchFields.find((field) => field.value === selectedSearchField);

  const onClickOutside = () => {
    if (!search.length) {
      searchActive = false;
    }

    searchFocus = false;
  };

  const onKeyUp = debounce(() => {
    if (!search.length) {
      QueryParams.delete('search');
    } else {
      if (selectedField?.value) {
        QueryParams.set('search', `${selectedField.value}:${search}`);
      } else {
        QueryParams.set('search', search);
      }
    }

    onChange(search, selectedField);
  }, 300);

  const onClick = () => {
    searchActive = true;
    searchRef.focus();
  };

  const onClearSearch = () => {
    search = '';
    QueryParams.delete('search');

    onChange(search, selectedField);
  };

  const selectField = (field) => {
    searchFields = searchFields.map((item) => {
      item.selected = field.value === item.value;

      return item;
    });

    selectedField = field;
    selectedSearchField = selectedField.value;
    search = '';

    QueryParams.set('search', `${field.value}:${search}`);
  };

  const initialize = () => {
    const params = QueryParams.get('search');
    const [context, value] = params?.split(':') || [];

    if (value) {
      search = value;
      selectedSearchField = context;
      selectedField = searchFields.find((field) => field.value === context);
      searchActive = true;
    } else {
      if (search.length) {
        if (selectedSearchField) {
          QueryParams.set('search', `${selectedSearchField}:${search}`);
        } else {
          QueryParams.set('search', search);
        }

        searchActive = true;
      }
    }
  };

  onMount(initialize);

  $: hidden = search.length === 0;
  $: initialize(selectedSearchField);
  $: searchActive = search.length > 0 || searchFocus;
</script>

<div
  class="search-filter"
  class:active={searchActive}
  class:focus={searchFocus}
  on:click={onClick}
  use:clickOutside={{ onClickOutside }}
  role="button"
  tabindex="0"
>
  <Icon name="search" />
  {#if !searchFocus && !search.length}
    <span class="text-placeholder">{$format('label.SEARCH')}</span>
  {/if}

  <input
    type="search"
    bind:this={searchRef}
    bind:value={search}
    on:keyup={onKeyUp}
    on:blur={() => (searchFocus = false)}
    on:focus={() => (searchFocus = true)}
    autocomplete="off"
  />

  <Icon
    name="x"
    color="secondary"
    class="pointer mx-1 {hidden ? 'opacity-0' : ''}"
    data-testid="clear-search-icon"
    on:click={onClearSearch}
    role="button"
    tabindex="0"
  />

  {#if searchFields.length}
    <div on:mousedown|preventDefault>
      <Dropdown class="field-dropdown">
        <DropdownToggle tag="div" caret>{$format(selectedField?.label) || ''}</DropdownToggle>
        <DropdownMenu>
          {#each searchFields as field}
            <DropdownItem active={field.value === selectedField?.value} on:click={() => selectField(field)}>
              {$format(field.label)}
            </DropdownItem>
          {/each}
        </DropdownMenu>
      </Dropdown>
    </div>
  {/if}
</div>

<style lang="scss" src="./search.scss"></style>
